import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';

const token = () => localStorage.getItem('token');

const fmt  = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtGBP = (v) => `£${parseFloat(v || 0).toFixed(2)}`;

const STATUS_STYLE = {
  paid:     { bg: '#d1fae5', color: '#065f46', label: 'Paid' },
  payable:  { bg: '#dbeafe', color: '#1e40af', label: 'Awaiting Payment' },
  pipeline: { bg: '#fef3c7', color: '#92400e', label: 'In Progress' },
};

function CommissionRow({ item }) {
  const ss = STATUS_STYLE[item.status] || STATUS_STYLE.pipeline;
  return (
    <tr style={{ borderBottom: '1px solid #EDF2F7' }}>
      <td style={{ padding: '12px 16px', fontSize: 14, color: '#0e1b2c' }}>{item.client_name}</td>
      <td style={{ padding: '12px 16px', fontSize: 13, color: '#718096' }}>{item.submitted_by || '—'}</td>
      <td style={{ padding: '12px 16px', fontSize: 13, color: '#718096' }}>{fmt(item.deal_created_at)}</td>
      <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 600, color: '#0e1b2c' }}>{fmtGBP(item.amount)}</td>
      <td style={{ padding: '12px 16px' }}>
        <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: ss.bg, color: ss.color }}>
          {ss.label}
        </span>
      </td>
      <td style={{ padding: '12px 16px', fontSize: 13, color: '#718096' }}>{item.paid_at ? fmt(item.paid_at) : '—'}</td>
    </tr>
  );
}

export default function IntroducerCommissionsPage() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [activeTab, setActiveTab] = useState('paid');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/commissions/dashboard', { headers: { Authorization: `Bearer ${token()}` } });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setData(d);
    } catch {
      setError('Failed to load commission data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="bc-page-layout">
      <Sidebar />
      <main className="main-content">
        <div style={{ padding: 40, color: '#718096', textAlign: 'center' }}>Loading…</div>
      </main>
    </div>
  );

  const summary  = data?.summary || {};
  const paid     = data?.paidHistory || [];
  const payable  = data?.payable || [];
  const pipeline = data?.pipeline || [];

  const rows = activeTab === 'paid' ? paid : activeTab === 'payable' ? payable : pipeline;

  const TABS = [
    { key: 'paid',     label: 'Paid',             count: summary.paidCount,     color: '#10b981' },
    { key: 'payable',  label: 'Awaiting Payment',  count: summary.payableCount,  color: '#3b82f6' },
    { key: 'pipeline', label: 'In Progress',       count: summary.pipelineCount, color: '#d97706' },
  ];

  return (
    <div className="bc-page-layout">
      <Sidebar />
      <main className="main-content">
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '32px 24px' }}>
          <div style={{ marginBottom: 28 }}>
            <h1 style={{ margin: 0, fontSize: 26, color: '#0e1b2c' }}>Commission Statements</h1>
            <p style={{ margin: '4px 0 0', color: '#718096', fontSize: 14 }}>Year to date</p>
          </div>

          {error && <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>{error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginBottom: 32 }}>
            {[
              { label: 'Paid (YTD)',       value: fmtGBP(summary.paidTotal),     count: summary.paidCount,     highlight: true },
              { label: 'Awaiting Payment', value: fmtGBP(summary.payableTotal),  count: summary.payableCount,  color: '#3b82f6' },
              { label: 'In Pipeline',      value: fmtGBP(summary.pipelineTotal), count: summary.pipelineCount, color: '#d97706' },
            ].map(s => (
              <div key={s.label} style={{ background: s.highlight ? '#0e1b2c' : '#fff', border: '1px solid #E2E8F0', borderRadius: 10, padding: '18px 20px' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: s.highlight ? '#10b981' : (s.color || '#0e1b2c') }}>{s.value}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: s.highlight ? '#fff' : '#0e1b2c', marginTop: 2 }}>{s.label}</div>
                <div style={{ fontSize: 11, color: s.highlight ? '#94a3b8' : '#a0aec0', marginTop: 2 }}>{s.count || 0} deal{s.count !== 1 ? 's' : ''}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', borderBottom: '2px solid #EDF2F7', marginBottom: 20 }}>
            {TABS.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                style={{ background: 'none', border: 'none',
                  borderBottom: activeTab === tab.key ? `3px solid ${tab.color}` : '3px solid transparent',
                  marginBottom: -2, padding: '8px 16px', fontSize: 13,
                  fontWeight: activeTab === tab.key ? 600 : 500,
                  color: activeTab === tab.key ? tab.color : '#718096',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                {tab.label}
                {tab.count > 0 && (
                  <span style={{ background: tab.color, color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10 }}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {rows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#718096' }}>No {activeTab} commissions yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 10, overflow: 'hidden', border: '1px solid #E2E8F0' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #E2E8F0' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Client</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Submitted By</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Deal Date</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Paid Date</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(item => <CommissionRow key={item.id} item={item} />)}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#f8fafc', borderTop: '2px solid #E2E8F0' }}>
                    <td colSpan={3} style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#718096' }}>Total</td>
                    <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 700, color: '#0e1b2c' }}>
                      {fmtGBP(rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0))}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
